import { Component, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslatePipe } from '../../core/i18n/translate.pipe';
import { EnterSubmitDirective } from '../../core/directives/enter-submit.directive';

export interface CancelLessonDialogResult {
  reason?: string;
  reasonType: 'NORMAL' | 'EMERGENCY';
  cancelledBy: 'STUDENT' | 'INSTRUCTOR' | 'OFFICE';
  forceNoConsequences?: boolean;
  noConsequences?: boolean;
}

export interface CancelLessonDialogData {
  instructorMode?: boolean;
}

@Component({
  selector: 'app-cancel-lesson-dialog',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatCheckboxModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    TranslatePipe
    ,EnterSubmitDirective
  ],
  template: `
    <h2 mat-dialog-title>{{ 'lessonCancel.title' | t }}</h2>
    <form appEnterSubmit [formGroup]="form" (ngSubmit)="confirm()">
    <mat-dialog-content>
      <div class="cancel-lesson-form">
        <mat-form-field>
          <mat-label>{{ 'lessonCancel.reason' | t }}</mat-label>
          <textarea matInput rows="3" formControlName="reason"></textarea>
        </mat-form-field>
        <mat-form-field>
          <mat-label>{{ 'lessonCancel.cancelledBy' | t }}</mat-label>
          <mat-select formControlName="cancelledBy">
            <mat-option value="STUDENT">{{ 'lessonCancel.cancelledBy.STUDENT' | t }}</mat-option>
            <mat-option value="INSTRUCTOR">{{ 'lessonCancel.cancelledBy.INSTRUCTOR' | t }}</mat-option>
            @if (!data?.instructorMode) {
              <mat-option value="OFFICE">{{ 'lessonCancel.cancelledBy.OFFICE' | t }}</mat-option>
            }
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <mat-label>{{ 'lessonCancel.reasonType' | t }}</mat-label>
          <mat-select formControlName="reasonType">
            <mat-option value="NORMAL">{{ 'lessonCancel.reasonType.NORMAL' | t }}</mat-option>
            <mat-option value="EMERGENCY">{{ 'lessonCancel.reasonType.EMERGENCY' | t }}</mat-option>
          </mat-select>
        </mat-form-field>
        <mat-form-field>
          <mat-label>{{ 'lessonCancel.consequenceMode' | t }}</mat-label>
          <mat-select formControlName="consequenceMode">
            <mat-option value="POLICY">{{ 'lessonCancel.consequenceMode.POLICY' | t }}</mat-option>
            <mat-option value="WITH_CONSEQUENCES">{{ 'lessonCancel.consequenceMode.WITH_CONSEQUENCES' | t }}</mat-option>
            <mat-option value="WITHOUT_CONSEQUENCES">{{ 'lessonCancel.consequenceMode.WITHOUT_CONSEQUENCES' | t }}</mat-option>
          </mat-select>
        </mat-form-field>
        <div class="preview">
          @if (form.controls.consequenceMode.value === 'WITH_CONSEQUENCES') {
            {{ 'lessonCancel.previewWithConsequences' | t }}
          } @else if (form.controls.consequenceMode.value === 'WITHOUT_CONSEQUENCES') {
            {{ 'lessonCancel.previewWithoutConsequences' | t }}
          } @else {
            {{ 'lessonCancel.previewPolicy' | t }}
          }
        </div>
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button type="button" (click)="dialogRef.close(null)">{{ 'common.cancel' | t }}</button>
      <button mat-flat-button type="submit" color="primary">{{ 'common.cancelLesson' | t }}</button>
    </mat-dialog-actions>
    </form>
  `,
  styles: `
    form { display: contents; }
    .cancel-lesson-form {
      display: grid;
      gap: 12px;
      min-width: 420px;
    }
    .preview {
      border: 1px solid #d7e1f5;
      border-radius: 6px;
      background: #f8fafc;
      color: #10233f;
      font-weight: 800;
      padding: 10px 12px;
    }
    @media (max-width: 700px) {
      .cancel-lesson-form {
        min-width: 280px;
      }
    }
  `
})
export class CancelLessonDialogComponent {
  readonly dialogRef = inject(MatDialogRef<CancelLessonDialogComponent, CancelLessonDialogResult | null>);
  readonly data = inject<CancelLessonDialogData | null>(MAT_DIALOG_DATA, { optional: true });
  private readonly fb = inject(FormBuilder);
  readonly form = this.fb.nonNullable.group({
    reason: [''],
    cancelledBy: [(this.data?.instructorMode ? 'INSTRUCTOR' : 'OFFICE') as 'STUDENT' | 'INSTRUCTOR' | 'OFFICE'],
    reasonType: ['NORMAL' as 'NORMAL' | 'EMERGENCY'],
    consequenceMode: ['POLICY' as 'POLICY' | 'WITH_CONSEQUENCES' | 'WITHOUT_CONSEQUENCES']
  });

  confirm(): void {
    const value = this.form.getRawValue();
    const consequencePayload =
      value.consequenceMode === 'POLICY'
        ? {}
        : {
            forceNoConsequences: value.consequenceMode === 'WITHOUT_CONSEQUENCES',
            noConsequences: value.consequenceMode === 'WITHOUT_CONSEQUENCES'
          };
    this.dialogRef.close({
      reason: value.reason.trim() || undefined,
      reasonType: value.reasonType,
      cancelledBy: value.cancelledBy,
      ...consequencePayload
    });
  }
}
